// Which screen the Monri return pages show. The DB state (webhook result) wins
// over the outcome in the return URL, which is only a hint about what the client did.
// Pure so the ordering, which is where both bugs lived, can be tested.

export type SessionPaymentView = "success" | "processing" | "cancelled" | "failed" | "holdRetry" | "refundPending";

export interface SessionPaymentState {
  status: string;
  paymentStatus: string;
  /** The card was charged but the session is gone; the money is being returned. */
  refundPending?: boolean;
}

/**
 * `pollWindowOver` is true once the page stopped polling. A therapist-created
 * hold whose card was declined stays `pending_hold` on purpose (the client may
 * retry from the same link), so it never reaches a terminal state on its own:
 * after a cancel at the gateway, or once polling gave up, it is shown as a
 * retryable failure instead of an endless spinner.
 */
export function resolveSessionPaymentView(
  data: SessionPaymentState | undefined,
  gatewayStatus: string,
  pollWindowOver: boolean,
): SessionPaymentView {
  if (data?.paymentStatus === "paid") return "success";
  // Checked before 'failed': the sweep marks the session failed/cancelled, and a
  // capture that arrives afterwards must not read as "try again".
  if (data?.refundPending) return "refundPending";
  if (data?.paymentStatus === "pending_hold" && data.status === "scheduled") {
    return gatewayStatus === "cancelled" || pollWindowOver ? "holdRetry" : "processing";
  }
  // User backed out at the gateway and the webhook has not marked it paid.
  // Also after the page released the abandoned session (status cancelled):
  // the client cancelled, it did not "fail".
  if (gatewayStatus === "cancelled") return "cancelled";
  if (data?.paymentStatus === "failed" || data?.status === "cancelled") return "failed";
  // status=success (or unknown) but not yet confirmed by the server.
  return "processing";
}

export type GroupPaymentView = "success" | "processing" | "failed" | "seatUnavailable" | "cancelled";

export interface GroupPaymentState {
  paymentStatus: string;
  cancelled: boolean;
}

export function resolveGroupPaymentView(
  data: GroupPaymentState | undefined,
  gatewayStatus: string,
): GroupPaymentView {
  // The webhook can approve a charge yet refuse the seat: money in, no seat.
  // This is NOT success, the refund is in progress.
  if (data && (data.cancelled || data.paymentStatus === "refunded")) return "seatUnavailable";
  if (data?.paymentStatus === "paid") return "success";
  // User backed out at the gateway. Checked before 'pending': an abandoned
  // payment stays 'pending' until the sweep, so testing 'pending' first left
  // this client on a spinner forever. Also before 'failed': the page itself
  // releases the abandoned attempt (status failed), which is a cancel.
  if (gatewayStatus === "cancelled") return "cancelled";
  if (data?.paymentStatus === "failed") return "failed";
  // Pending / pending_transfer / unknown: not yet confirmed by the webhook.
  return "processing";
}
